import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { format, parse } from 'date-fns';

import { calulatedDiscountValue } from '../../../utils/summaryCalculation';
import { getReservationSummaryByPaymentRequestUniqeid } from '../../../services/paymentRequest';
import { bookingsStatusColorCode } from '../../../utils/statusColor';
import useWebData from '../../../hooks/useWebData';
import usePleaseWait from '../../../hooks/usePleaseWait';
import { iconValues } from '../../../utils/iconValues';
import { tenantId } from '../../../config/api';


const ViewBooking = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search)
  const uniqeid = queryParams?.get('id') ?? 0;

  const navigate = useNavigate();

  const { webData }: any = useWebData();
  const { setPleasWaitPopup }: any = usePleaseWait();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reservationData, setReservationData] = useState<any | null>(null);
  const [statusBg, setStatusBg] = useState<string>("");
  const [statusTextColor, setStatusTextColor] = useState<string>("");
  const [latestPickup, setLatestPickup] = useState<Date>(new Date());
  const [latestReturn, setLatestReturn] = useState<Date>(new Date());
  const [checkOutDateTime, setCheckOutDateTime] = useState<Date>(new Date());
  const [checkInDateTime, setCheckInDateTime] = useState<Date>(new Date());
  const [taxList, setTaxList] = useState<any[]>([]);
  const [securityDepositAmount, setSecurityDepositAmount] = useState<number>(0.0);
  const [includedMiles, setIncludedMiles] = useState<number>(0);
  const [totalDrivenMiles, setTotalDrivenMiles] = useState<number>(0);

  const [appliedDiscountList, setAppliedDiscountList] = useState<any[]>([
    {
      id: 0,
      discountId: 0,
      discountDefinition: "",
      discountType: "Percentage",
      discountAmount: 0.00,
      discount: 0.00,
      manualDiscount: false,
      discountApplied: false
    },
    {
      id: 0,
      discountId: 0,
      discountDefinition: "Manual Discount",
      discountType: "Amount",
      discountAmount: 0.00,
      discount: 0.00,
      manualDiscount: true,
      discountApplied: false
    }
  ]);

  useEffect(() => {
    if (uniqeid) {
      setIsLoading(true);
      setPleasWaitPopup(true);
      getReservationSummaryByPaymentRequestUniqeid(uniqeid, tenantId)
        .then(res => {
          setReservationData(res);

          let color = {
            colorBg: "#000000",
            colorText: "#000000"
          };

          color = bookingsStatusColorCode?.find((color: any) => color?.label === res?.status.trim()?.toLowerCase()) || {
            colorBg: "#000000",
            colorText: "#000000"
          };

          setStatusBg(color.colorBg);
          setStatusTextColor(color.colorText);

          const combinedPickupDateTime = parse(`${res?.pickupDate} ${res?.pickupTime}`, 'MM/dd/yyyy hh:mm a', new Date());
          setLatestPickup(combinedPickupDateTime);

          const combinedReturnDateTime = parse(`${res?.returnDate} ${res?.returnTime}`, 'MM/dd/yyyy hh:mm a', new Date());
          setLatestReturn(combinedReturnDateTime);

          if (res?.checkOutDate !== "" && res?.checkOutDate !== null) {
            const combinedCheckoutDateTime = parse(`${res?.checkOutDate} ${res?.checkOutTime}`, 'MM/dd/yyyy hh:mm a', new Date());
            setCheckOutDateTime(combinedCheckoutDateTime);
          }

          if (res?.checkInDate !== "" && res?.checkInDate !== null) {
            const combinedCheckinDateTime = parse(`${res?.checkInDate} ${res?.checkInTime}`, 'MM/dd/yyyy hh:mm a', new Date());
            setCheckInDateTime(combinedCheckinDateTime);
          }

          setSecurityDepositAmount(res?.securityDepositAmount - res?.securityDepositAmountPaid > 0 ? res?.securityDepositAmount - res?.securityDepositAmountPaid : 0);


          const addedTaxList = [];
          if (res?.mandatoryAdditionalChargesAmount && res?.mandatoryAdditionalChargesAmount > 0) {
            addedTaxList.push({
              taxName: "Additional Charge Fee",
              taxAmount: res?.mandatoryAdditionalChargesAmount ?? "",
              taxType: "Amount",
              selectionType: "One Time",
              basePrice: res?.mandatoryAdditionalChargesAmount
            });
          }

          if (res?.locationFeeAmount && res?.locationFeeAmount > 0) {
            addedTaxList.push({
              taxName: "Location Tax Fee",
              taxAmount: res?.locationFeePercentage ?? "",
              taxType: "Percent",
              selectionType: "One Time",
              basePrice: res?.locationFeeAmount
            });
          }

          if (res?.taxAmount && res?.taxAmount > 0) {
            addedTaxList.push({
              taxName: "Tax Fee",
              taxAmount: res?.taxPercentage ?? "",
              taxType: "Percent",
              selectionType: "One Time",
              basePrice: res?.taxAmount
            });
          }

          if (res?.governmentTaxAmount && res?.governmentTaxAmount > 0) {
            addedTaxList.push({
              taxName: "Government Tax Fee",
              taxAmount: res?.governmentTaxPercentage ?? "",
              taxType: "Percent",
              selectionType: "One Time",
              basePrice: res?.governmentTaxAmount
            });
          }

          setTaxList(addedTaxList);
          setIsLoading(false);
          setPleasWaitPopup(false);

          setIncludedMiles(
            (res?.totalHours * res?.totalDistanceLimit)
            + (res?.totalDays * res?.distanceLimitPerDay)
            + (res?.totalWeeks * res?.distanceLimitPerWeek)
            + (res?.totalMonths * res?.distanceLimitPerMonth)
          );

          let totalDistanceDrives = 0;
          if (res?.vehicleReplacementReportForAgreement && res?.vehicleReplacementReportForAgreement?.length > 0) {
            res?.vehicleReplacementReportForAgreement?.forEach((item: any, index: number) => {
              if (item?.checkOutDate && item?.checkInDate) {
                totalDistanceDrives += (item?.odometerAtCheckIn - item?.odometerAtCheckOut);
              }

              if (res?.vehicleReplacementReportForAgreement?.length - 1 === index) {
                setTotalDrivenMiles(totalDistanceDrives);
              }
            });
          }

          let basePrice = 0;
          let totalOfSelectedVehicleClass = 0;
          if (res.totalMonths > 0) {
            basePrice += (res.totalMonths * res.monthlyRate);
          }

          if (res.totalWeeks > 0) {
            basePrice += (res.totalWeeks * res.weeklyRate);
          }

          if (res.totalDays > 0) {
            basePrice += (res.totalDays * res.dailyRate);
          }

          if (res.totalHours > 0) {
            basePrice += (res.totalHours * res.hourlyRate);
          }

          totalOfSelectedVehicleClass = basePrice;

          res?.reservationDiscountsForAgreement?.forEach((item: any) => {
            let discountPrice = 0;
            if (item?.discountType === "Amount") {
              discountPrice = item?.discountAmount ?? 0;
            } else {
              discountPrice = ((basePrice * (item?.discountAmount || 0)) / 100) || 0;
            }

            basePrice -= discountPrice;
          });

          const couponDiscount = res?.reservationDiscountsForAgreement?.find((item: any) => !item?.manualDiscount);
          const manualDiscount = res?.reservationDiscountsForAgreement?.find((item: any) => item?.manualDiscount);

          let couponDiscountValue = 0.00;
          if (couponDiscount) {
            couponDiscountValue = calulatedDiscountValue(couponDiscount?.discountAmount, couponDiscount?.discountType, totalOfSelectedVehicleClass);
          }

          let manualDiscountValue = 0.00;
          if (manualDiscount) {
            manualDiscountValue = calulatedDiscountValue(manualDiscount?.discountAmount, manualDiscount?.discountType, totalOfSelectedVehicleClass);
          }

          setAppliedDiscountList([
            {
              id: couponDiscount ? couponDiscount?.id : 0,
              discountId: couponDiscount ? couponDiscount?.discountId : 0,
              discountDefinition: couponDiscount ? couponDiscount?.discountDefinition : 0,
              discountType: couponDiscount ? couponDiscount?.discountType : "Percentage",
              discountAmount: couponDiscount ? couponDiscount?.discountAmount : 0.00,
              discount: couponDiscountValue,
              manualDiscount: false,
              discountApplied: couponDiscount ? true : false
            },
            {
              id: manualDiscount ? manualDiscount?.id : 0,
              discountId: 0,
              discountDefinition: manualDiscount ? manualDiscount?.discountDefinition : 0,
              discountType: manualDiscount ? manualDiscount?.discountType : "Amount",
              discountAmount: manualDiscount ? manualDiscount?.discountAmount : 0.00,
              discount: manualDiscountValue,
              manualDiscount: true,
              discountApplied: manualDiscount ? true : false
            }
          ]);

        })
        .catch((error) => {
          setIsLoading(false);
          setPleasWaitPopup(false);
          if (error?.response?.status === 400) {
            if (error?.response?.data === "Link is expired or invalid.") {
              navigate(`/public/booking/not-available?id=${uniqeid}`, { replace: true });
            } else {
              navigate(`not-found`, { replace: true });
            }
          }
          console.log(error);
        });
    } else {
      navigate(`not-found`, { replace: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqeid])


  return (
    <div className="mt-[140px]">
      {
        isLoading
          ? (
            <div className="min-h-[calc(100vh-140px)] h-[calc(100vh-140px)] bg-[#F2F7FA] px-[1rem] py-[0.5rem] my-[1.5rem]">
              <div
                className="bg-white w-full h-full border border-[#ff6926] rounded-lg"
                style={{
                  borderColor: webData?.websiteHeaderColor
                }}
              >
                <div className="h-full flex items-center justify-center gap-x-[2rem] px-[2rem] py-[1rem] min-h-[24rem]">
                  <p className="text-sm text-gray-400">Loading...</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="min-h-[calc(100vh-82px)] bg-[#F2F7FA] px-[1rem] py-[0.5rem] my-[1.5rem]">
                <div className="bg-white w-full h-full border border-[#E4E7EC] rounded-lg">
                  <div className="flex flex-col lg:flex-row gap-x-[2rem] gap-y-[4rem] px-[0.5rem] lg:px-[2rem] py-[1rem]">
                    <div className="w-full lg:flex-1 py-[0.75rem]">
                      <div>
                        <h5>
                          <span>Booking Number - </span>
                          <strong>{reservationData?.bookingNumber || "-"}</strong>
                        </h5>
                      </div>

                      <div className="w-full mt-[1.25rem]">
                        <span className={`text-[14px] font-semibold inline-block mb-2 text-[#1D2939]`}>
                          Booked Vehicle
                        </span>

                        <hr className={`w-full `} />

                        <div className={`flex flex-col sm:flex-row items-center gap-x-[2rem] bg-transparent p-2 my-2 w-full h-full min-h-max rounded-lg shadow-sm border border-[#E4E7EC]`}>
                          <img
                            src={reservationData?.vehicleClassImage}
                            alt="vehicle"
                            className="w-[230px] rounded-md"
                          />

                          <div className="flex flex-col px-2 w-full sm:w-[34%] gap-1.5">
                            <span className={`text-center sm:text-start text-[16px] font-semibold text-[#1D2939]`}>
                              {reservationData?.vehicleClassLabel}
                            </span>

                            <div className={`flex flex-col items-center sm:items-start gap-1.5 text-xs text-black-rgba`}>
                              {
                                reservationData
                                  ?.features
                                  ?.sort((a: any, b: any) => a?.order - b?.order)
                                  ?.map((feature: any, indexFeat: number) => {
                                    const iconData = iconValues?.find((icon) => icon.value === feature?.icon);
                                    return (
                                      <div
                                        key={indexFeat}
                                        className={`flex items-center gap-1 text-xs text-black-rgba`}
                                      >
                                        {
                                          iconData && (
                                            <span className={`material-symbols-outlined text-[16px]`}>{iconData.icon}</span>
                                          )
                                        }
                                        <span>{feature?.websiteLabel}</span>
                                      </div>
                                    )
                                  })
                              }
                            </div>
                          </div>
                        </div>

                        <span className={`text-[14px] font-semibold inline-block my-2 text-[#1D2939]`}>
                          Customer Information
                        </span>

                        <hr className={`w-full `} />

                        <div className="w-full my-2">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>First Name</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.firstName || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>Email Address</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.customerEmail || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>Street</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.customerStreet || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>City</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.customerCity || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>Zip</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.customerZipCode || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>Birthday</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.birthdate || "-"}</span>
                              </div>

                            </div>
                            <div className="space-y-4">
                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>Last Name</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.lastName || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>Phone Number</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.customerPhoneNumber || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>Street 2</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{""}</span>
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.customerStreet2 || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>State</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.customerState || "-"}</span>
                              </div>

                              <div>
                                <span className={`text-xs font-medium text-[#1D2939]`}>Country</span><br />
                                <span className={`text-xs font-medium text-black-rgba`}>{reservationData?.customerCountry || "-"}</span>
                              </div>
                            </div>

                          </div>
                        </div>

                      </div>
                    </div>

                    <div className="w-full lg:w-[35%] xl:w-2/6 py-[0.5rem]">
                      <div className={`w-full bg-transparent p-2 flex flex-col gap-1.5 rounded-lg shadow-sm border border-[#E4E7EC]`}>
                        <div className={`flex justify-between items-center py-4 border-b border-[#E4E7EC]`}>
                          <span className={`text-sm font-bold text-[#1D2939]`}>Summary</span>

                          <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${statusBg} ${statusTextColor} border border-[#E4E7EC]`}>
                            {reservationData?.status || "-"}
                          </span>
                        </div>

                        {
                          reservationData
                            ? (
                              <>
                                <div className="flex flex-col gap-1.5 py-2">
                                  <div className="flex flex-col justify-between items-start mb-4">
                                    <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Branch</span>
                                    <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>{reservationData?.branchName || "-"}</span>
                                  </div>

                                  {
                                    reservationData?.vehicleReplacedReturnDates
                                      ?.map((item: any, index: number, arr: any[]) => (
                                        <div
                                          key={index}
                                          className={`flex flex-col justify-between items-start ${index === arr.length - 1 ? "mb-4" : ""}`}
                                        >
                                          <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Replaced Date {index + 1}</span>
                                          <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>
                                            {item &&
                                              format(
                                                new Date(item),
                                                "EEEE, MMMM dd, yyyy @ KK:mm a"
                                              )
                                            }
                                          </span>
                                        </div>
                                      ))
                                  }


                                  {
                                    (reservationData?.checkOutDate !== "" && reservationData?.checkOutDate !== null) && (
                                      <div className="flex flex-col justify-between items-start">
                                        <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Pickup Date</span>
                                        <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>
                                          {
                                            reservationData?.vehicleReplacedPickupDates?.length > 0
                                              ? format(
                                                new Date(reservationData?.vehicleReplacedPickupDates[0]),
                                                "EEEE, MMMM dd, yyyy @ KK:mm a"
                                              )
                                              : format(
                                                new Date(checkOutDateTime),
                                                "EEEE, MMMM dd, yyyy @ KK:mm a"
                                              )
                                          }
                                        </span>
                                      </div>
                                    )
                                  }

                                  <div className="flex flex-col justify-between items-start">
                                    <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Initial Pickup Date</span>
                                    <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>{format(latestPickup, "EEEE, MMMM dd, yyyy @ KK:mm a")}</span>
                                  </div>

                                  <div className="flex flex-col justify-between items-start mb-4">
                                    <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Pickup Location</span>
                                    <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>{reservationData?.pickupLocationName || "-"}</span>
                                  </div>

                                  {
                                    (reservationData?.checkInDate !== "" && reservationData?.checkInDate !== null) && (
                                      <div className="flex flex-col justify-between items-start">
                                        <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Return Date</span>
                                        <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>
                                          {
                                            format(
                                              new Date(checkInDateTime),
                                              "EEEE, MMMM dd, yyyy @ KK:mm a"
                                            )
                                          }
                                        </span>
                                      </div>
                                    )
                                  }

                                  <div className="flex flex-col justify-between items-start">
                                    <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Initial Return Date</span>
                                    <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>{format(latestReturn, "EEEE, MMMM dd, yyyy @ KK:mm a")}</span>
                                  </div>

                                  <div className="flex flex-col justify-between items-start mb-4">
                                    <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Return Location</span>
                                    <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>{reservationData?.returnLocationName || "-"}</span>
                                  </div>

                                  {
                                    (
                                      reservationData.vehicleName !== null) && (
                                      <div className="flex flex-col justify-between items-start mb-4">
                                        <span className={`text-xs font-medium break-all w-full text-left text-black-rgba`}>Active Vehicle</span>
                                        <span className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>
                                          {reservationData.vehicleName || "-"}
                                        </span>
                                      </div>
                                    )
                                  }

                                  {
                                    <div className="flex flex-col mb-4">
                                      <span className={`text-xs font-medium break-all text-left text-black-rgba`}>Vehicle Class</span>
                                      {
                                        reservationData?.vehicleReplacementReportForAgreement?.map((item: any, index: number) => (
                                          <span key={index} className={`text-xs font-medium break-all w-full text-left text-[#1D2939]`}>{(index === 0 ? "" : " -> ") + item?.vehicleClassLabel}
                                          </span>
                                        ))
                                      }
                                    </div>
                                  }

                                  <div className="flex justify-between gap-x-[0.25rem]">
                                    {
                                      <div className="flex-1 flex flex-col justify-between items-start">
                                        <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>Included Distance</span>
                                        <span className={`text-xs font-medium break-all flex-1 text-left text-[#1D2939]`}>{`${includedMiles} ${includedMiles > 1 ? "miles" : "mile"}`}</span>
                                      </div>
                                    }

                                    {
                                      ((reservationData?.checkOutDate !== "" && reservationData?.checkOutDate !== null) && (reservationData?.checkInDate !== "" && reservationData?.checkOutDate !== null))
                                      &&
                                      (
                                        <div className="flex-1 flex flex-col justify-between items-start">
                                          <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>Total Driven Distance</span>
                                          <span className={`text-xs font-medium break-all flex-1 text-left text-[#1D2939]`}>{`${totalDrivenMiles} ${totalDrivenMiles > 1 ? "miles" : "mile"}`}</span>
                                        </div>
                                      )
                                    }
                                  </div>
                                </div>

                                <hr className={`w-full`} />

                                <div className={`pt-2`}>
                                  <span className={`text-sm font-bold text-[#1D2939]`}>Price Details</span>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                  {
                                    reservationData?.totalMonths !== 0 && (
                                      <div className="flex justify-between items-start w-full">
                                        <span className={`text-xs font-medium break-all flex-1 text-left text-[#1D2939]`}>{reservationData?.totalMonths}x Months</span>
                                        <span className={`text-xs font-medium break-all flex-1 text-right text-[#1D2939]`}>${reservationData?.monthlyRate?.toFixed(2)}</span>
                                        <span className={`text-xs font-medium break-all flex-1 text-right text-[#1D2939]`}>${(reservationData?.totalMonths * reservationData?.monthlyRate)?.toFixed(2)}</span>
                                      </div>
                                    )
                                  }

                                  {
                                    reservationData?.totalWeeks !== 0 && (
                                      <div className="flex justify-between items-start w-full">
                                        <span className={`text-xs font-medium break-all flex-1 text-left text-[#1D2939]`}>{reservationData?.totalWeeks}x Weeks</span>
                                        <span className={`text-xs font-medium break-all flex-1 text-right text-[#1D2939]`}>${reservationData?.weeklyRate?.toFixed(2)}</span>
                                        <span className={`text-xs font-medium break-all flex-1 text-right text-[#1D2939]`}>${(reservationData?.totalWeeks * reservationData?.weeklyRate)?.toFixed(2)}</span>
                                      </div>
                                    )
                                  }

                                  {
                                    reservationData?.totalDays !== 0 && (
                                      <div className="flex justify-between items-start w-full">
                                        <span className={`text-xs font-medium break-all flex-1 text-left text-[#1D2939]`}>{reservationData?.totalDays}x Days</span>
                                        <span className={`text-xs font-medium break-all flex-1 text-right text-[#1D2939]`}>${reservationData?.dailyRate?.toFixed(2)}</span>
                                        <span className={`text-xs font-medium break-all flex-1 text-right text-[#1D2939]`}>${(reservationData?.totalDays * reservationData?.dailyRate)?.toFixed(2)}</span>
                                      </div>
                                    )
                                  }

                                  {
                                    reservationData?.totalHours !== 0 && (
                                      <div className="flex justify-between items-start w-full">
                                        <span className={`text-xs font-medium break-all flex-1 text-left text-[#1D2939]`}>{reservationData?.totalHours}x Hours</span>
                                        <span className={`text-xs font-medium break-all flex-1  text-right text-[#1D2939]`}>${reservationData?.hourlyRate?.toFixed(2)}</span>
                                        <span className={`text-xs font-medium break-all flex-1 text-right text-[#1D2939]`}>${(reservationData?.totalHours * reservationData?.hourlyRate)?.toFixed(2)}</span>
                                      </div>
                                    )
                                  }

                                  <hr className={`w-full `} />

                                  {
                                    appliedDiscountList
                                      ?.filter((item: any) => item?.discountApplied)
                                      ?.map((item: any, index: number) => {
                                        return (
                                          <div key={index} className="flex justify-between items-center">
                                            <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{item?.discountDefinition}:</span>
                                            <span className={`text-xs font-medium break-all w-[30%] text-right text-[#1D2939]`}>-${parseFloat(item?.discount)?.toFixed(2)}</span>
                                          </div>
                                        )
                                      })
                                  }

                                  {
                                    reservationData?.chargeFuleToClient && reservationData?.fuelLevelCharge > 0 && (
                                      <div>
                                        <span className={`text-sm font-bold text-[#1D2939]`}>Equipment & Services</span>
                                        <div className="flex justify-between items-start mb-2">
                                          <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>Charge For Fuel {reservationData?.fuelLevelCharge || 0}x:</span>
                                          <span className={`text-xs font-medium break-all w-[30%] text-right text-[#1D2939]`}>${reservationData?.fuelCharge?.toFixed(2)}</span>
                                        </div>
                                      </div>
                                    )
                                  }

                                  {
                                    reservationData?.additionalCategoryForAgreement?.map((item: any, j: number) => {
                                      return (
                                        <div key={j}>
                                          <span className={`text-sm font-bold text-[#1D2939]`}>{item?.categoryName}</span>

                                          {
                                            item?.additionalChargesForAgreement?.map((value: any, i: number) => (
                                              <div key={i} className="flex justify-between items-center mb-2">
                                                <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{value?.name} {value?.quantity > 1 ? value?.quantity + "x " : ''}:</span>
                                                <span className={`text-xs font-medium break-all w-[30%] text-right text-[#1D2939]`}>${(value?.quantity * value?.basePrice)?.toFixed(2)}</span>
                                              </div>
                                            ))
                                          }
                                        </div>
                                      )
                                    })
                                  }

                                  {
                                    taxList?.length > 0 && (
                                      <div>
                                        <span className={`text-sm font-bold text-[#1D2939]`}>Miscellaneous</span>
                                        {
                                          taxList?.map((item: any, index: number) => (
                                            <div
                                              key={index}
                                              className="flex justify-between items-center"
                                            >
                                              <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{item?.taxName}{`${item?.taxType === "Percent" ? ` (${item?.taxAmount}%)` : ""}`}:</span>
                                              <span className={`text-xs font-medium break-all w-[30%] text-right text-[#1D2939]`}>${parseFloat(item?.basePrice)?.toFixed(2)}</span>
                                            </div>
                                          ))
                                        }
                                      </div>
                                    )
                                  }
                                </div>

                                <hr className={`w-full !mt-2 `} />

                                <div className={`flex justify-between items-center py-2`}>
                                  <span className={`text-sm font-bold text-black-rgba`}>Total</span>
                                  <span className={`text-sm font-bold text-[#1D2939]`}> ${reservationData?.totalPrice?.toFixed(2)}</span>
                                </div>

                                {(!reservationData?.securityDepositPaid && securityDepositAmount > 0)
                                  && (
                                    <>
                                      <hr className={`w-full `} />

                                      <div className={`flex justify-between items-center py-4`}>
                                        <span className={`text-sm font-bold text-black-rgba`}>Security Deposit</span>
                                        <span className={`text-sm font-bold ${!reservationData?.securityDepositPaid ? "text-red-500" : "text-[#1D2939]"}`}>${securityDepositAmount ? securityDepositAmount?.toFixed(2) : 0.00}</span>
                                      </div>
                                    </>
                                  )
                                }

                                {
                                  reservationData?.reservationPaymentsForAgreement
                                    ?.filter((item: any) => item?.paymentType === "Payment")
                                    ?.length > 0 && (
                                    <>
                                      <hr className={`w-full `} />

                                      <div className={`pt-2`}>
                                        <span className={`text-sm font-bold text-[#1D2939]`}>Payments</span>
                                      </div>

                                      <div className="flex flex-col">
                                        {
                                          reservationData?.reservationPaymentsForAgreement
                                            ?.filter((item: any) => item?.paymentType === "Payment")
                                            ?.map((item: any, index: number) => {
                                              let subHeaderTest = "";

                                              if (item?.cClastFourDigit) {
                                                subHeaderTest = `${item?.cClastFourDigit || ""} ${item?.brand} - ${item?.reference || ""}`;
                                              } else if (item?.brand) {
                                                subHeaderTest = `${item?.brand || ""} - ${item?.reference || ""}`;
                                              } else {
                                                subHeaderTest = `${item?.reference || ""}`;
                                              }

                                              return (
                                                <div key={index} className="flex justify-between items-center">
                                                  <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{subHeaderTest}:</span>
                                                  <span className={`text-xs font-medium break-all w-[30%] text-right text-[#1D2939]`}>${parseFloat(item?.amount)?.toFixed(2)}</span>
                                                </div>
                                              )
                                            })
                                        }
                                      </div>
                                    </>
                                  )
                                }

                                {
                                  reservationData?.reservationPaymentsForAgreement
                                    ?.filter((item: any) => item?.paymentType === "Security Deposit")
                                    ?.length > 0 && (
                                    <>
                                      <hr className={`w-full `} />

                                      <div className={`pt-2`}>
                                        <span className={`text-sm font-bold text-[#1D2939]`}>Security Deposits</span>
                                      </div>

                                      <div className="flex flex-col">
                                        {
                                          reservationData?.reservationPaymentsForAgreement
                                            ?.filter((item: any) => item?.paymentType === "Security Deposit")
                                            ?.map((item: any, index: number) => {
                                              let subHeaderTest = "";

                                              if (item?.cClastFourDigit) {
                                                subHeaderTest = `${item?.cClastFourDigit || ""} ${item?.brand} - ${item?.reference || ""} (Security Deposit)`;
                                              } else if (item?.brand) {
                                                subHeaderTest = `${item?.brand || ""} - ${item?.reference || ""} (Security Deposit)`;
                                              } else {
                                                subHeaderTest = `${item?.reference || ""} (Security Deposit)`;
                                              }

                                              return (
                                                <div key={index} className="flex justify-between items-center mb-2">
                                                  <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{subHeaderTest}:</span>
                                                  <span className={`text-xs font-medium break-all w-[30%] text-right text-[#1D2939]`}>${parseFloat(item?.amount)?.toFixed(2)}</span>
                                                </div>
                                              )
                                            })
                                        }
                                      </div>
                                    </>
                                  )
                                }

                                {
                                  reservationData?.reservationRefundsForAgreement?.length > 0 && (
                                    <>
                                      <hr className={`w-full `} />

                                      <div className={`pt-2`}>
                                        <span className={`text-sm font-bold text-[#1D2939]`}>Refunds</span>
                                      </div>

                                      <div className="flex flex-col">
                                        {
                                          reservationData?.reservationRefundsForAgreement?.map((item: any, index: number) => {
                                            const isSecurityDepositRefund = item?.refundType === "Security Deposit";
                                            let subHeaderTest = "";

                                            if (item?.cClastFourDigit) {
                                              subHeaderTest = `${item?.cClastFourDigit || ""} ${item?.brand || ""} - ${item?.reference || ""} ${isSecurityDepositRefund ? "(Security Deposit)" : ""}`;
                                            } else if (item?.brand) {
                                              subHeaderTest = `${item?.brand || ""} - ${item?.reference || ""} ${isSecurityDepositRefund ? "(Security Deposit)" : ""}`;
                                            } else {
                                              subHeaderTest = `${item?.reference || ""} ${isSecurityDepositRefund ? "(Security Deposit)" : ""}`;
                                            }

                                            return (
                                              <div key={index} className="flex justify-between items-center mb-2">
                                                <span className={`text-xs font-medium break-all flex-1 text-left text-black-rgba`}>{subHeaderTest}:</span>
                                                <span className={`text-xs font-medium break-all w-[30%] text-right text-[#1D2939]`}>${Math.abs(parseFloat(item?.amount))?.toFixed(2)}</span>
                                              </div>
                                            )
                                          })
                                        }
                                      </div>
                                    </>
                                  )
                                }

                                <hr className={`w-full `} />

                                <div className={`flex justify-between items-center py-2`}>
                                  <span className={`text-sm font-bold text-black-rgba`}> Outstanding Balance</span>
                                  <span className={`text-sm font-bold text-[#1D2939]`}>${reservationData?.outstandingBalance?.toFixed(2) ?? 0.00}</span>
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-[24rem] flex items-center justify-center">
                                <p className="text-sm text-gray-400">Loading...</p>
                              </div>
                            )
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
      }
    </div>
  )
}

export default ViewBooking